import { Period } from "@/@types/generics/Period";
import { RiskClassification } from "@/@types/sintegrada/RiskClassification";
import { Specialty } from "@/@types/sintegrada/Specialty";
import { User } from "@/@types/sintegrada/User";
import { getSintegradaServerApi } from "@/services/api";
import { ResumeGrouped } from "@/services/esus/attendanceService";
import { MedicalAttendanceTypeEnum } from "@/utils/enums/MedicalAttendanceTypeEnum";

export type SpecializedMedicalAttendanceDashboardParams = {
	uf: string;
	ibgeCode: string;
	startDate: string;
	endDate: string;
	cnes?: string;
	youngerAge?: number;
	olderAge?: number;
	professionalId?: number;
	professionalCns?: string;
	professionalName?: string;
};

export type SpecializedMedicalAttendanceDashboardData = {
	totalMedicalAttendancesRequested: number;
	totalMedicalAttendancesEvaluated: number;
	aggregatedByPeriodAndMedicalAttendanceType: ResumeGrouped<
		Period,
		ResumeGrouped<MedicalAttendanceTypeEnum, object>
	>[];
	aggregatedByDayOfWeek: ResumeGrouped<0 | 1 | 2 | 3 | 4 | 5 | 6, object>[];
	aggregatedByHourOfDay: ResumeGrouped<number, object>[];
	aggregatedByGender: ResumeGrouped<"M" | "F", object>[];
	aggregatedByRiskClassification: ResumeGrouped<RiskClassification | null, object>[];
	aggregatedByShift: ResumeGrouped<string, object>[];
	evasions: number;
	averageTimeInSecondsByRiskClassification: ResumeGrouped<RiskClassification | null, object>[];
	averageWaitTimeInSecondsByRiskClassification: ResumeGrouped<
		RiskClassification | null,
		object
	>[];
	aggregatedByProfessionalsAndMedicalAttendanceType: ResumeGrouped<
		User,
		ResumeGrouped<MedicalAttendanceTypeEnum, object>
	>[];
	aggregatedBySpecialty: ResumeGrouped<Specialty, object>[];
	averageTimeInSecondsByProfessional: ResumeGrouped<User, object>[];
	averageWaitTime: number;
	minWaitTime: number;
	maxWaitTime: number;
	averageTime: number;
	minTime: number;
	maxTime: number;
	averageMedicalAttendancesPerDay: number;
	totalMedicalAttendancesOnTime: number;
	totalMedicalAttendancesOutTime: number;
	totalMedicalAttendancesWithoutRiskClassification: number;
};

export async function getSpecializedMedicalAttendanceDashboard({
	...params
}: SpecializedMedicalAttendanceDashboardParams) {
	const url = `/attendance/specialized-medical-attendance-dashboard`;

	const { data } = await getSintegradaServerApi().get<SpecializedMedicalAttendanceDashboardData>(
		url,
		params
	);

	// Garantir que todas as propriedades sejam arrays válidos
	const defaultData: SpecializedMedicalAttendanceDashboardData = {
		totalMedicalAttendancesRequested: 0,
		totalMedicalAttendancesEvaluated: 0,
		aggregatedByPeriodAndMedicalAttendanceType: [],
		aggregatedByDayOfWeek: [],
		aggregatedByHourOfDay: [],
		aggregatedByGender: [],
		aggregatedByRiskClassification: [],
		aggregatedByShift: [],
		evasions: 0,
		averageTimeInSecondsByRiskClassification: [],
		averageWaitTimeInSecondsByRiskClassification: [],
		aggregatedByProfessionalsAndMedicalAttendanceType: [],
		aggregatedBySpecialty: [],
		averageTimeInSecondsByProfessional: [],
		averageWaitTime: 0,
		minWaitTime: 0,
		maxWaitTime: 0,
		averageTime: 0,
		minTime: 0,
		maxTime: 0,
		averageMedicalAttendancesPerDay: 0,
		totalMedicalAttendancesOnTime: 0,
		totalMedicalAttendancesOutTime: 0,
		totalMedicalAttendancesWithoutRiskClassification: 0,
	};

	if (!data) {
		return defaultData;
	}

	// Validar e garantir que todas as propriedades de array sejam arrays válidos
	return {
		totalMedicalAttendancesRequested: data.totalMedicalAttendancesRequested ?? 0,
		totalMedicalAttendancesEvaluated: data.totalMedicalAttendancesEvaluated ?? 0,
		aggregatedByPeriodAndMedicalAttendanceType: Array.isArray(
			data.aggregatedByPeriodAndMedicalAttendanceType
		)
			? data.aggregatedByPeriodAndMedicalAttendanceType
			: [],
		aggregatedByDayOfWeek: Array.isArray(data.aggregatedByDayOfWeek)
			? data.aggregatedByDayOfWeek
			: [],
		aggregatedByHourOfDay: Array.isArray(data.aggregatedByHourOfDay)
			? data.aggregatedByHourOfDay
			: [],
		aggregatedByGender: Array.isArray(data.aggregatedByGender) ? data.aggregatedByGender : [],
		aggregatedByRiskClassification: Array.isArray(data.aggregatedByRiskClassification)
			? data.aggregatedByRiskClassification
			: [],
		aggregatedByShift: Array.isArray(data.aggregatedByShift) ? data.aggregatedByShift : [],
		evasions: data.evasions ?? 0,
		averageTimeInSecondsByRiskClassification: Array.isArray(
			data.averageTimeInSecondsByRiskClassification
		)
			? data.averageTimeInSecondsByRiskClassification
			: [],
		averageWaitTimeInSecondsByRiskClassification: Array.isArray(
			data.averageWaitTimeInSecondsByRiskClassification
		)
			? data.averageWaitTimeInSecondsByRiskClassification
			: [],
		aggregatedByProfessionalsAndMedicalAttendanceType: Array.isArray(
			data.aggregatedByProfessionalsAndMedicalAttendanceType
		)
			? data.aggregatedByProfessionalsAndMedicalAttendanceType
			: [],
		aggregatedBySpecialty: Array.isArray(data.aggregatedBySpecialty)
			? data.aggregatedBySpecialty
			: [],
		averageTimeInSecondsByProfessional: Array.isArray(data.averageTimeInSecondsByProfessional)
			? data.averageTimeInSecondsByProfessional
			: [],
		averageWaitTime: data.averageWaitTime ?? 0,
		minWaitTime: data.minWaitTime ?? 0,
		maxWaitTime: data.maxWaitTime ?? 0,
		averageTime: data.averageTime ?? 0,
		minTime: data.minTime ?? 0,
		maxTime: data.maxTime ?? 0,
		averageMedicalAttendancesPerDay: data.averageMedicalAttendancesPerDay ?? 0,
		totalMedicalAttendancesOnTime: data.totalMedicalAttendancesOnTime ?? 0,
		totalMedicalAttendancesOutTime: data.totalMedicalAttendancesOutTime ?? 0,
		totalMedicalAttendancesWithoutRiskClassification:
			data.totalMedicalAttendancesWithoutRiskClassification ?? 0,
	} as SpecializedMedicalAttendanceDashboardData;
}
